// features/intelligence/domain-selector.js
// ═══════════════════════════════════════════════════════════
// DOMAIN SELECTOR — the page the user lands on when they
// tap "Intelligence" in the sidebar.
//
// Renders inside the existing app shell (the shift-main pane)
// — does NOT use the .di-root grid. The existing AppSidebar
// stays visible to the left.
//
// Two states (toggle for demo, real version reads
// userRecord.domains?.length):
//   - Returning: 3 active domain cards with today's pulse,
//     "Available Official Domains" queue
//   - New: 3 catalog cards with descriptions + "Create
//     Personal Workspace" tile
//
// Only Trading is wired through to the briefing today. Deal
// + Survival are visible but selecting them is a no-op until
// their sample bundles land.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

// Sample data — official domains. When real domain
// records arrive this comes from the database.
//
// Domains with `url` route to a separate page (Strategic
// Intelligence is the first — its workspace lives at
// /strategic-playbook with its own theme). Domains without
// `url` open inside the app shell via onSelectDomain.
const DOMAINS = [
  {
    key: 'trading',
    name: 'Trading Intelligence',
    accent: 'gold',
    badge: 'official',
    practitioners: 847,
    description: 'Pattern recognition, signal analysis, and personal intelligence for active traders. Connects to your studies and criteria. Learns from your outcomes.',
    pulse: 'FOMC minutes release at <em class="gold">2:00 PM ET</em>. Language shift probability elevated. Your <em class="gold">Squeeze study</em> conditions forming in 3 setups.',
    built: true,
  },
  {
    key: 'strategic',
    name: 'Strategic Intelligence',
    accent: 'purple',
    badge: 'official',
    practitioners: 312,
    description: 'Decision dossiers, pre-built shock playbooks, and elite-thinker operating systems. For leaders, founders, teachers, and anyone navigating big calls.',
    pulse: '<em class="purple">Indiana 16-0 + national title</em> flips the asymmetric-bet thesis from "year-2 caveat" to "doubly proved." Pillar 2 confidence rises 65 → 88.',
    built: true,
    url: '/strategic-playbook',
  },
  {
    key: 'deal',
    name: 'Deal Intelligence',
    accent: 'steel',
    badge: 'official',
    practitioners: 1204,
    description: 'Creative real estate finance intelligence. Deal analysis, seller psychology, strategy matching, and conversation intelligence for every deal.',
    pulse: '30-year fixed <em class="steel">−12bp overnight</em>. First meaningful move in 6 weeks. Seller motivation rising in your Tampa market.',
    built: false,
  },
  {
    key: 'survival',
    name: 'Survival Intelligence',
    accent: 'sage',
    badge: 'official',
    practitioners: 623,
    description: 'Outdoor and wilderness intelligence for every skill level. Plan trips, read environments, build skills, respond to emergencies.',
    pulse: '<em class="terra">Red flag conditions</em> active in Florida through Thursday. AQI elevated. Fire weather watch issued for your region.',
    built: false,
  },
  {
    key: 'wealth',
    name: 'Wealth Intelligence',
    accent: 'teal',
    badge: 'official',
    practitioners: 1856,
    description: 'Compounding strategy, tax law, and instrument design — for every wealth tier from Roth to Saylor. Public simulator derives your hypothesis. Case studies show how the rich actually do it.',
    pulse: '10Y at <em class="teal">4.61%</em> shifts SBLOC math against leveraged plays. <em class="teal">Roth conversion window</em> narrows in November. Three new case studies validated this week.',
    built: true,
    url: '/wealth-building',
  },
  {
    key: 'redtape',
    name: 'Red Tape Intelligence',
    accent: 'terra',
    badge: 'official',
    practitioners: 1043,
    description: 'Beat the bureaucracy that quietly costs you money. Appeal an over-assessed property tax bill, answer an IRS notice, recover a withheld security deposit, or dissolve a dead LLC — each a guided, deadline-aware tool that tells you plainly when to DIY and when to get a pro.',
    pulse: 'Property-tax appeal windows open in <em class="terra">12 Cook County townships</em> this month. IRS <em class="terra">CP2000</em> notices commonly overstate the balance — most are winnable with the right response.',
    built: true,
    url: '/bureaucracy',
  },
];

const QUEUE = [
  'Emergency Intelligence (Anchor)',
  'Screenwriting and Story Intelligence',
  'Dentist Practice Intelligence',
  'CPA and Tax Advisory Intelligence',
  'Real Estate Agent Intelligence',
];

export default function DomainSelector({ userRecord, onSelectDomain }) {
  // Real version: derive from userRecord.domains?.length > 0.
  // For Layer 1 we let Tim toggle so both states are previewable.
  const [view, setView] = useState('returning');
  const greeting = userRecord?.first_name || userRecord?.username || 'Tim';

  function handleCardClick(domain) {
    if (!domain.built) {
      // Surface a clear, low-noise notice. Layer 2 = real domain.
      if (typeof window !== 'undefined') {
        window.alert(`${domain.name} is coming soon. Trading Intelligence is the first domain available.`);
      }
      return;
    }
    // Domains with a separate URL navigate out of the app shell
    // (Strategic Intelligence has its own themed workspace).
    if (domain.url && typeof window !== 'undefined') {
      window.location.href = domain.url;
      return;
    }
    onSelectDomain?.(domain.key);
  }

  return (
    <div className="di-selector-root">
      <SelectorTopbar />

      <div className="di-selector-content">
        <ViewToggle view={view} onChange={setView} />

        {view === 'returning' ? (
          <ReturningView greeting={greeting} onCardClick={handleCardClick} />
        ) : (
          <NewView onCardClick={handleCardClick} />
        )}
      </div>
    </div>
  );
}

// ── Topbar ──────────────────────────────────────────────────
function SelectorTopbar() {
  // Match the date format the mockup uses; real version uses
  // user's locale + timezone.
  const dateString = 'Friday, Apr 18 · 7:04 AM';
  return (
    <div className="di-selector-topbar">
      <div className="di-selector-title">Your Domains</div>
      <div className="di-selector-date">{dateString}</div>
      <div className="di-selector-search">
        <span className="di-selector-search-icon">◎</span>
        <span className="di-selector-search-text">Search or add a domain…</span>
      </div>
    </div>
  );
}

// ── State toggle (demo) ─────────────────────────────────────
function ViewToggle({ view, onChange }) {
  return (
    <div className="di-selector-toggle">
      <button
        type="button"
        className={`di-selector-toggle-btn${view === 'new' ? ' active' : ''}`}
        onClick={() => onChange('new')}
      >
        New User View
      </button>
      <button
        type="button"
        className={`di-selector-toggle-btn${view === 'returning' ? ' active' : ''}`}
        onClick={() => onChange('returning')}
      >
        Returning User View
      </button>
    </div>
  );
}

// ── Returning user view ─────────────────────────────────────
function ReturningView({ greeting, onCardClick }) {
  const builtCount = DOMAINS.filter((d) => d.built).length;
  const activeCountWord =
    builtCount === 1 ? 'One domain' :
    builtCount === 2 ? 'Two domains' :
    builtCount === 3 ? 'Three domains' :
    builtCount === 4 ? 'Four domains' :
    `${builtCount} domains`;
  return (
    <>
      <div className="di-selector-header">
        <div className="di-selector-eyebrow">Good morning, {greeting}</div>
        <div className="di-selector-h1">Your Intelligence Domains</div>
        <div className="di-selector-sub">
          {activeCountWord} active · Each domain&apos;s pulse updated as of this morning
        </div>
      </div>

      <div className="di-domain-grid">
        {DOMAINS.map(d => (
          <DomainTile
            key={d.key}
            domain={d}
            mode="pulse"
            isActiveDomain={d.key === 'trading'}
            onClick={() => onCardClick(d)}
          />
        ))}
      </div>

      <div className="di-domain-grid" style={{ marginTop: 6 }}>
        <AddDomainTile
          title="Add a Domain"
          sub="Explore 9 official domains or create a personal workspace"
        />
      </div>

      <div className="di-queue-label">Available Official Domains</div>
      {QUEUE.map(name => (
        <div key={name} className="di-queue-row">
          <div className="di-queue-name">{name}</div>
          <div className="di-queue-status">Coming soon</div>
          <button type="button" className="di-queue-btn">Notify Me</button>
        </div>
      ))}
    </>
  );
}

// ── New user view ───────────────────────────────────────────
function NewView({ onCardClick }) {
  return (
    <>
      <div className="di-selector-header">
        <div className="di-selector-eyebrow">Welcome to Shift</div>
        <div className="di-selector-h1">What are you working on?</div>
        <div className="di-selector-sub">
          Choose one or more domains. Each domain is a fully-built intelligence
          layer — data sources, community knowledge, and a personal workstation
          configured for your specific work.
        </div>
      </div>

      <div className="di-domain-grid">
        {DOMAINS.map(d => (
          <DomainTile
            key={d.key}
            domain={d}
            mode="description"
            onClick={() => onCardClick(d)}
            ctaLabel="Start Here →"
          />
        ))}
        <AddDomainTile
          icon="◇"
          title="Create Personal Space"
          sub="Build a custom workspace for any domain not yet officially available. Your Workstation — connected to your own sources."
        />
      </div>
    </>
  );
}

// ── Domain tile ─────────────────────────────────────────────
function DomainTile({ domain: d, mode, isActiveDomain, onClick, ctaLabel = 'Open →' }) {
  return (
    <div
      className={`di-domain-tile${isActiveDomain ? ' active' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
    >
      {isActiveDomain && <div className="di-domain-active-dot" />}
      <div className={`di-domain-accent ${d.accent}`} />
      <div className="di-domain-body">
        <div className="di-domain-top">
          <div className="di-domain-name">{d.name}</div>
          <div className={`di-domain-badge ${d.badge}`}>{d.badge}</div>
        </div>

        {mode === 'pulse' && (
          <div className="di-domain-pulse">
            <div className="di-domain-pulse-label">Today&apos;s Pulse</div>
            <div
              className="di-domain-pulse-text"
              dangerouslySetInnerHTML={{ __html: d.pulse }}
            />
          </div>
        )}
        {mode === 'description' && (
          <div className="di-domain-desc">{d.description}</div>
        )}

        <div className="di-domain-footer">
          <div className="di-domain-meta">
            {d.practitioners.toLocaleString()} practitioners{mode === 'pulse' ? ' · Live data' : ''}
          </div>
          <button type="button" className={`di-domain-cta ${d.accent}`}>{ctaLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add tile ───────────────────────────────────────────────
function AddDomainTile({ icon = '+', title, sub }) {
  return (
    <button type="button" className="di-add-tile">
      <div className="di-add-tile-icon">{icon}</div>
      <div className="di-add-tile-title">{title}</div>
      <div className="di-add-tile-sub">{sub}</div>
    </button>
  );
}
